import Image from 'next/image'
import { ShoppingCart } from 'lucide-react'
import { AffiliateCardCTA } from '@/components/blog/affiliate-card-cta'
import { PickBadgePill } from '@/components/blog/pick-badge-pill'
import { getProductByAsin } from '@/lib/affiliate-card-lookup'
import type { ParsedAffiliateProduct } from '@/lib/blog-content-parts'

interface AffiliateCardBlockProps {
  products: ParsedAffiliateProduct[]
  slug: string
  maxItems?: number
}

// Abbreviations whose trailing period must NOT be read as a sentence end
// (e.g. "St. Augustine", "approx. 5,000 sq. ft."). Matched against the word
// immediately before a candidate boundary.
const ABBREVIATIONS = /(?:^|\s)(?:st|dr|mr|mrs|ms|vs|etc|approx|no|inc|co|ltd|jr|sr|oz|lb|lbs|sq|ft|in|gal|pt|qt)\.$/i

function firstSentence(text: string): string {
  // Walk each candidate sentence-ender that is followed by whitespace or
  // end-of-string. A period mid-number ("7.5%", "$59.88") is followed by a
  // digit, so it never matches. A period after a known abbreviation ("St.")
  // is skipped so we keep scanning to the real sentence end.
  const boundary = /[.!?](?:\s|$)/g
  let m: RegExpExecArray | null
  while ((m = boundary.exec(text)) !== null) {
    const candidate = text.slice(0, m.index + 1)
    if (ABBREVIATIONS.test(candidate)) continue
    return candidate.trim()
  }
  return text.trim()
}

export function AffiliateCardBlock({ products, slug, maxItems = 3 }: AffiliateCardBlockProps) {
  const items = products.slice(0, maxItems)
  if (items.length === 0) return null

  return (
    <div className="my-8 ds-glass rounded-[20px] p-4 sm:p-5 not-prose">
      <h3 className="text-xs font-bold text-green-700 uppercase tracking-wide mb-3">
        Recommended products
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {items.map((product, idx) => {
          const enrichment = getProductByAsin(product.asin)
          const imageUrl = enrichment?.imageUrl ?? null
          const tagline = product.description ? firstSentence(product.description) : null

          return (
            <div
              key={`${product.asin ?? 'no-asin'}-${idx}`}
              className="flex flex-row md:flex-col gap-3 md:gap-0 rounded-2xl bg-white/55 border border-white/55 p-3 backdrop-blur-md transition-[transform,background] duration-200 hover:-translate-y-0.5 hover:bg-white/75"
            >
              <div className="flex items-center justify-center bg-white rounded-lg w-24 h-24 md:w-full md:h-auto md:aspect-square md:mb-3 border border-white/70 overflow-hidden shrink-0">
                {imageUrl ? (
                  <Image
                    src={imageUrl}
                    alt={product.name}
                    width={160}
                    height={160}
                    loading="lazy"
                    sizes="(max-width: 768px) 96px, 160px"
                    className="w-full h-full object-contain p-1 md:p-2"
                  />
                ) : (
                  <div className="flex items-center justify-center w-full h-full bg-green-50">
                    <ShoppingCart className="w-8 h-8 text-green-400" />
                  </div>
                )}
              </div>

              <div className="flex flex-col flex-1 min-w-0">
                {product.badge && (
                  <div className="mb-1.5">
                    <PickBadgePill badge={product.badge} size="sm" />
                  </div>
                )}
                <h4 className="text-sm font-bold text-gray-900 leading-snug mb-1 line-clamp-2">
                  {product.name}
                </h4>

                {tagline && (
                  <p className="text-xs text-gray-600 leading-snug mb-2 md:mb-3 line-clamp-2">
                    {tagline}
                  </p>
                )}

                <div className="mt-auto pt-1">
                  <AffiliateCardCTA
                    href={product.href}
                    name={product.name}
                    slug={slug}
                    asin={product.asin}
                    position={idx + 1}
                  />
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
